import { useEffect, useRef, useState } from 'react'
import {
  TextInput,
  PasswordInput,
  Button,
  Paper,
  Title,
  Text,
  Anchor,
  Stack,
  Center,
  Box,
  Alert,
  Divider,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconFingerprint } from '@tabler/icons-react'
import { useAuthStore } from '../../stores/authStore'
import { authClient } from '../../api/authClient'

function showWelcomeToast() {
  notifications.show({
    title: 'Welcome!',
    message: 'You have successfully signed in.',
    color: 'green',
    autoClose: 2000,
  })
}

export function LoginView() {
  const { isLoading, error, login, loginWithPasskey, clearError } = useAuthStore()

  const [passkeysAvailable, setPasskeysAvailable] = useState(false)
  const [passkeySubmitting, setPasskeySubmitting] = useState(false)
  const autofillControllerRef = useRef<AbortController | null>(null)

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value) => (value.length < 1 ? 'Password is required' : null),
    },
  })

  // Discover passkey support and, if the browser can show them in the autofill dropdown,
  // register a pending conditional-UI request as soon as the page mounts. Selecting a
  // passkey there completes login with no further clicks; typing a password instead just
  // proceeds with the form below, and the pending request is aborted on submit/unmount.
  useEffect(() => {
    let cancelled = false

    async function setupPasskeys() {
      if (!authClient.passkeys.isSupported()) {
        return
      }

      let config
      try {
        config = await authClient.passkeys.getConfig()
      } catch {
        return
      }
      if (cancelled || !config.enabled) {
        return
      }
      setPasskeysAvailable(true)

      const autofillSupported = await authClient.passkeys.isAutofillSupported()
      if (cancelled || !autofillSupported) {
        return
      }

      const controller = new AbortController()
      autofillControllerRef.current = controller
      const success = await loginWithPasskey({ useBrowserAutofill: true, signal: controller.signal })
      if (success) {
        showWelcomeToast()
      }
    }

    setupPasskeys()

    return () => {
      cancelled = true
      autofillControllerRef.current?.abort()
    }
    // Mount-only: re-running this on every store update would re-register the conditional
    // UI request repeatedly.
  }, [])

  const handleSubmit = form.onSubmit(async (values) => {
    autofillControllerRef.current?.abort()
    clearError()
    const success = await login(values.email, values.password)

    if (success) {
      showWelcomeToast()
    }
  })

  const handlePasskeyLogin = async () => {
    // Deliberately does not abort the pending conditional-UI request here — SimpleWebAuthn's
    // startAuthentication() already cancels any in-flight ceremony internally the moment
    // it's called (WebAuthnAbortService.createNewAbortSignal()). Aborting it ourselves first
    // was found to leave the browser's WebAuthn session in a state where the immediately
    // following explicit call also resolved with no credential ("Authentication was not
    // completed") instead of showing the picker.
    clearError()
    setPasskeySubmitting(true)
    const success = await loginWithPasskey({ useBrowserAutofill: false })
    setPasskeySubmitting(false)

    if (success) {
      showWelcomeToast()
    }
  }

  return (
    <Center style={{ minHeight: '100vh' }}>
      <Box w={400}>
        <Title ta="center" mb="md">
          Accounts - Paper Airplane
        </Title>
        <Text c="dimmed" size="sm" ta="center" mb="xl">
          Sign in to your account
        </Text>

        <Paper withBorder shadow="md" p="xl" radius="md">
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Login failed"
              color="red"
              mb="md"
              withCloseButton
              onClose={clearError}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack>
              <TextInput
                label="Email"
                placeholder="you@example.com"
                required
                autoComplete="username webauthn"
                disabled={isLoading}
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                autoComplete="current-password"
                disabled={isLoading}
                {...form.getInputProps('password')}
              />
              <Button type="submit" fullWidth mt="md" loading={isLoading && !passkeySubmitting}>
                Sign in
              </Button>
            </Stack>
          </form>

          {passkeysAvailable && (
            <>
              <Divider label="or" labelPosition="center" my="md" />
              <Button
                variant="default"
                fullWidth
                leftSection={<IconFingerprint size={18} />}
                loading={passkeySubmitting}
                disabled={isLoading && !passkeySubmitting}
                onClick={handlePasskeyLogin}
              >
                Sign in with a passkey
              </Button>
              {/* The button ignores the email field and can't enroll, which staging testing
                  showed reads as confusing without saying so. */}
              <Text c="dimmed" size="xs" ta="center" mt="xs">
                Already set up a passkey? Use it here. You can add one in Settings after
                signing in.
              </Text>
            </>
          )}

          <Text c="dimmed" size="sm" ta="center" mt="lg">
            <Anchor component="button" type="button" size="sm">
              Forgot password?
            </Anchor>
          </Text>
        </Paper>
      </Box>
    </Center>
  )
}
